import React from 'react';
import {
  Card,
  CardHeader,
  Container,
  Row,
  Col,
  FormGroup,
  Input,
  Button,
  Table,
  Spinner,
  Badge,
  Alert,
} from 'reactstrap';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import Header from '../../components/Headers/Header.js';
import { axiosGet, axiosPost, axiosPatch } from '../../util/apiConfig';

const money = (n) => Number(n || 0).toLocaleString();

class TeacherPayout extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      month: moment().format('YYYY-MM'),
      rows: [],
      loading: false,
      computing: false,
      message: '',
      errorMsg: '',
      categoryFilter: '',
    };
  }

  componentDidMount() {
    this.loadRows();
  }

  loadRows = () => {
    const { month } = this.state;
    if (!month) return;
    this.setState({ loading: true, errorMsg: '' });
    axiosGet(`payout?month=${month}`)
      .then((res) => {
        this.setState({ rows: Array.isArray(res.data) ? res.data : [], loading: false });
      })
      .catch((err) => {
        this.setState({ loading: false, rows: [], errorMsg: (err && err.message) || 'Could not load payouts' });
      });
  };

  compute = () => {
    const { month } = this.state;
    if (!month) return;
    this.setState({ computing: true, message: '', errorMsg: '' });
    axiosPost('payout/compute', { month })
      .then((res) => {
        this.setState({ computing: false, message: (res && res.message) || 'Computed' });
        this.loadRows();
      })
      .catch((err) => {
        this.setState({ computing: false, errorMsg: (err && err.message) || 'Compute failed' });
      });
  };

  updateRowField = (id, field, value) => {
    this.setState((prev) => ({
      rows: prev.rows.map((r) => (r.teacherPayoutId === id ? { ...r, [field]: value } : r)),
    }));
  };

  saveAdjustments = (row, extra = {}) => {
    // A finalized row only allows notes edits — miscellaneous/deduction must
    // be omitted from the payload here, since sending them (even unchanged)
    // trips the backend's changingAmounts guard and rejects the whole PATCH,
    // which would also block the Notes save this omission exists to allow.
    const locked = row.status === 'finalized';
    const payload = locked
      ? { notes: row.notes || '', ...extra }
      : {
        miscellaneous: row.miscellaneous === '' ? 0 : Number(row.miscellaneous),
        deduction: row.deduction === '' ? 0 : Number(row.deduction),
        notes: row.notes || '',
        ...extra,
      };
    axiosPatch(`payout/${row.teacherPayoutId}`, payload)
      .then((res) => {
        const data = (res && res.data) || {};
        this.setState((prev) => ({
          rows: prev.rows.map((r) =>
            r.teacherPayoutId === row.teacherPayoutId
              ? {
                ...r,
                total: data.total !== undefined ? data.total : r.total,
                counts: data.conversions !== undefined ? { ...r.counts, conversions: data.conversions } : r.counts,
                conversionsOverride:
                    data.conversionsOverride !== undefined ? data.conversionsOverride : r.conversionsOverride,
              }
              : r
          ),
          message: 'Adjustment saved',
        }));
      })
      .catch((err) => this.setState({ errorMsg: (err && err.message) || 'Could not save adjustment' }));
  };

  // Conversions is a count, not a components.$ figure — send it as its own field
  // (payout.controller.js recomputes the $ amount server-side from the stored rate) rather
  // than folding it into the generic misc/deduction/notes payload above.
  saveConversionsOverride = (row) => {
    const raw = row.counts && row.counts.conversions;
    const conversions = raw === '' || raw === null || raw === undefined ? '' : Number(raw);
    this.saveAdjustments(row, { conversions });
  };

  updateConversions = (id, value) => {
    this.setState((prev) => ({
      rows: prev.rows.map((r) => (r.teacherPayoutId === id ? { ...r, counts: { ...r.counts, conversions: value } } : r)),
    }));
  };

  finalize = (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Finalize payout for ${row.teacherName || row.teacherId}? This locks the row.`)) return;
    axiosPost(`payout/${row.teacherPayoutId}/finalize`, {})
      .then(() => this.loadRows())
      .catch((err) => this.setState({ errorMsg: (err && err.message) || 'Could not finalize' }));
  };

  csvData = () => {
    // Always exports the full month, independent of the on-screen category
    // filter — payroll export shouldn't silently drop teachers because a
    // filter was left set from browsing.
    const { rows } = this.state;
    return [
      [
        'Teacher', 'Email', 'ClassId', 'Category', 'Month', '50min', '25min', 'Trials', 'NoShows', 'TrialNoShows',
        'Conversions', 'Class50', 'Class25', 'Trial', 'NoShow', 'TrialNoShow', 'Conversion',
        'Miscellaneous', 'Deduction', 'Total', 'Status', 'Notes',
      ],
      ...rows.map((r) => [
        r.teacherName, r.teacherEmail, r.classId, r.teacherCategory, r.month,
        r.counts.count50, r.counts.count25, r.counts.trialCount, r.counts.noShowCount, r.counts.trialNoShowCount,
        r.counts.conversions,
        r.components.class50, r.components.class25, r.components.trial, r.components.noShow, r.components.trialNoShow,
        r.components.conversion,
        r.miscellaneous, r.deduction, r.total, r.status, r.notes,
      ]),
    ];
  };

  visibleRows = () => {
    const { rows, categoryFilter } = this.state;
    if (!categoryFilter) return rows;
    return rows.filter((r) => (r.teacherCategory || 'Uncategorized') === categoryFilter);
  };

  render() {
    const { month, loading, computing, message, errorMsg, categoryFilter } = this.state;
    const rows = this.visibleRows();
    const grandTotal = rows.reduce((s, r) => s + Number(r.total || 0), 0);
    const categories = [...new Set(this.state.rows.map((r) => r.teacherCategory || 'Uncategorized'))].sort();

    return (
      <>
        <Header />
        <Container className="mt--7" fluid>
          <Card className="shadow">
            <CardHeader className="border-0">
              <Row className="align-items-center">
                <Col xs="8">
                  <h3 className="mb-0">Teacher Monthly Payout</h3>
                </Col>
                <Col className="text-right" xs="4">
                  <CSVLink data={this.csvData()} filename={`teacher-payout-${month}.csv`} className="btn btn-sm btn-outline-primary">
                    Export CSV
                  </CSVLink>
                </Col>
              </Row>
              <Row className="mt-3 align-items-end">
                <Col md="3">
                  <FormGroup className="mb-0">
                    <label className="form-control-label">Month</label>
                    <Input type="month" value={month} onChange={(e) => this.setState({ month: e.target.value }, this.loadRows)} />
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup className="mb-0">
                    <label className="form-control-label">Category</label>
                    <Input
                      type="select"
                      value={categoryFilter}
                      onChange={(e) => this.setState({ categoryFilter: e.target.value })}
                    >
                      <option value="">All categories</option>
                      {categories.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <Button color="primary" onClick={this.loadRows} disabled={loading || computing}>
                    {loading ? 'Refreshing…' : 'Refresh Data'}
                  </Button>
                  {loading ? <Spinner size="sm" className="ml-2" /> : null}
                </Col>
              </Row>
              {message ? <Alert color="info" className="mt-3 mb-0 py-2">{message}</Alert> : null}
              {errorMsg ? <Alert color="danger" className="mt-3 mb-0 py-2">{errorMsg}</Alert> : null}
            </CardHeader>

            <div style={{ overflowX: 'auto' }}>
              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th scope="col">Teacher</th>
                    <th scope="col">Category</th>
                    <th scope="col" className="text-center">50m</th>
                    <th scope="col" className="text-center">25m</th>
                    <th scope="col" className="text-center">Trials</th>
                    <th scope="col" className="text-center">No-show</th>
                    <th scope="col" className="text-center">Trials No-show</th>
                    <th scope="col" style={{ minWidth: '70px' }}>Conv.</th>
                    <th scope="col" className="text-right">Class50</th>
                    <th scope="col" className="text-right">Class25</th>
                    <th scope="col" className="text-right">Trial</th>
                    <th scope="col" className="text-right">NoShow</th>
                    <th scope="col" className="text-right">TrialNoShow</th>
                    <th scope="col" className="text-right">Conv$</th>
                    <th scope="col" style={{ minWidth: '90px' }}>Misc</th>
                    <th scope="col" style={{ minWidth: '90px' }}>Deduct</th>
                    <th scope="col" style={{ minWidth: '160px' }}>Notes</th>
                    <th scope="col" className="text-right">Total</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="19" className="text-center"><Spinner /></td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan="19" className="text-center text-muted">No payouts for this month. Click “Compute / Refresh”.</td></tr>
                  ) : (
                    rows.map((r) => {
                      const locked = r.status === 'finalized';
                      return (
                        <tr key={r.teacherPayoutId}>
                          <td>
                            <span className="font-weight-bold">{r.teacherName || r.teacherId}</span>
                            <br />
                            <small className="text-muted">{r.teacherEmail}</small>
                            <br />
                            {r.rates && r.rates.source === 'teacher' ? (
                              <Badge color="success" className="mt-1">Custom rates</Badge>
                            ) : (
                              <Badge color="warning" className="mt-1" title="No payout settings defined for this teacher — using the global default rates">
                                No settings defined — default
                              </Badge>
                            )}
                          </td>
                          <td>{r.teacherCategory || <span className="text-muted">—</span>}</td>
                          <td className="text-center">{r.counts.count50}</td>
                          <td className="text-center">{r.counts.count25}</td>
                          <td className="text-center">{r.counts.trialCount}</td>
                          <td className="text-center">{r.counts.noShowCount}</td>
                          <td className="text-center">{r.counts.trialNoShowCount}</td>
                          <td>
                            <Input
                              type="number"
                              bsSize="sm"
                              disabled={locked}
                              value={r.counts.conversions}
                              title={
                                r.conversionsOverride !== null && r.conversionsOverride !== undefined
                                  ? `Overridden — auto-matched value was ${r.counts.conversionsComputed}`
                                  : 'Auto-matched from trial attribution'
                              }
                              onChange={(e) => this.updateConversions(r.teacherPayoutId, e.target.value)}
                              onBlur={() => this.saveConversionsOverride(r)}
                            />
                            {r.conversionsOverride !== null && r.conversionsOverride !== undefined ? (
                              <small className="text-warning">overridden (auto: {r.counts.conversionsComputed})</small>
                            ) : null}
                          </td>
                          <td className="text-right">{money(r.components.class50)}</td>
                          <td className="text-right">{money(r.components.class25)}</td>
                          <td className="text-right">{money(r.components.trial)}</td>
                          <td className="text-right">{money(r.components.noShow)}</td>
                          <td className="text-right">{money(r.components.trialNoShow)}</td>
                          <td className="text-right">{money(r.components.conversion)}</td>
                          <td>
                            <Input
                              type="number"
                              bsSize="sm"
                              disabled={locked}
                              value={r.miscellaneous}
                              onChange={(e) => this.updateRowField(r.teacherPayoutId, 'miscellaneous', e.target.value)}
                              onBlur={() => this.saveAdjustments(r)}
                            />
                          </td>
                          <td>
                            <Input
                              type="number"
                              bsSize="sm"
                              disabled={locked}
                              value={r.deduction}
                              onChange={(e) => this.updateRowField(r.teacherPayoutId, 'deduction', e.target.value)}
                              onBlur={() => this.saveAdjustments(r)}
                            />
                          </td>
                          <td>
                            <Input
                              type="textarea"
                              rows="2"
                              bsSize="sm"
                              placeholder="Remarks before sending…"
                              value={r.notes || ''}
                              onChange={(e) => this.updateRowField(r.teacherPayoutId, 'notes', e.target.value)}
                              onBlur={() => this.saveAdjustments(r)}
                            />
                          </td>
                          <td className="text-right font-weight-bold">{money(r.total)}</td>
                          <td>
                            {locked ? (
                              <Badge color="success">Finalized</Badge>
                            ) : (
                              <Button size="sm" color="success" outline onClick={() => this.finalize(r)}>
                                Finalize
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
                {rows.length > 0 ? (
                  <tfoot>
                    <tr>
                      <td colSpan="17" className="text-right font-weight-bold">Grand total</td>
                      <td className="text-right font-weight-bold">{money(grandTotal)}</td>
                      <td />
                    </tr>
                  </tfoot>
                ) : null}
              </Table>
            </div>
          </Card>
        </Container>
      </>
    );
  }
}

export default TeacherPayout;
